import { useEffect } from 'react'
import i18n, { type DefaultNamespace } from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import { ErrorMessage, Form as FormikForm, useFormikContext } from 'formik'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { InfoColored } from '@dhl/icons'
import { CheckboxInput, NumberFieldInput } from '@dhl/inputs'
import { CaptchaInput } from '@dhl/captcha-input'
import { AddressDetails, ContactDetails, TransportTypeInput, ReturnTypeInput, LabelTypeInput } from '~/components'
import { PickupDateInput, PaperSection } from '~/widgets'
import { formatNumber, WeekDay } from '~/helpers'
import { TranslationContext, useReturnFormReset, useTranslationContext } from '~/hooks'
import {
  getPackageConstraints,
  allowedHandoffCountries,
  useEveningTimeWindows,
  type ReturnRequest,
  type ReturnPortal,
} from '~/model'
import { config } from '~/config'

type Props = {
  returnPortal: ReturnPortal
  onCountryChange: (countryCode: string) => void
}

export const getErrorMessage = (fieldName: string) => (
  <Typography
    sx={({ palette }) => ({
      color: palette.error.main,
    })}
  >
    <ErrorMessage name={fieldName} />
  </Typography>
)

export const ReturnForm = ({ returnPortal, onCountryChange }: Props) => {
  const { t } = useTranslation()
  const isCcc = returnPortal.businessUnitCode === 'ccc'
  const theme = useTheme()

  const eveningTimeWindows = useEveningTimeWindows(returnPortal)
  const allowedCountries = allowedHandoffCountries(returnPortal)
  const translationContext = useTranslationContext(returnPortal.businessUnitCode)
  const tComponents = {
    // eslint-disable-next-line jsx-a11y/anchor-has-content,jsx-a11y/anchor-is-valid
    a: <a />,
  }
  const { values: returnRequest, isSubmitting, errors, dirty } = useFormikContext<ReturnRequest>()
  useReturnFormReset(returnPortal)

  const {
    address: { countryCode, postalCode },
    transportType,
    returnType,
  } = returnRequest

  const isAddressAndContactValid =
    dirty &&
    !errors.address &&
    !errors.firstName &&
    !errors.lastName &&
    !errors.email &&
    !errors.reference &&
    !!returnRequest.reference // additional valid check needed because form errors are sometimes empty on initial render

  useEffect(() => {
    onCountryChange(countryCode.key)
  }, [countryCode.key, onCountryChange])

  const { weight = undefined, size = undefined } =
    returnType && transportType ? getPackageConstraints(isCcc, returnType, transportType) : {}

  const showLabelTypeInput = !isCcc && returnType === 'service_point'

  const getCheckBox = (fieldName: string, label: string | React.ReactNode, icon?: React.ReactNode) => (
    <>
      <FormControlLabel
        data-test={`checkbox-${fieldName}`}
        sx={{
          display: 'flex',
          marginLeft: 0,
        }}
        control={<CheckboxInput name={fieldName} sx={{ padding: `2px 1` }} />}
        label={
          <Typography sx={{ paddingTop: 1, display: 'inline-block' }} component="span">
            {label}
            {icon}
          </Typography>
        }
      />

      {getErrorMessage(fieldName)}
    </>
  )

  let packageSizeConfirmedLabel
  if (weight) {
    if (!isCcc && (returnType === 'service_point' || returnType === 'consumer_pickup')) {
      const labelKey = returnType === 'service_point' ? 'labelBnlServicePoint' : 'labelBnlConsumerPickup'
      packageSizeConfirmedLabel = t(`inputs.packageSizeConfirmed.${labelKey}`, {
        weight: formatNumber(weight),
      })
    } else {
      const myParcelKey = transportType === 'pallet' ? 'myPallet' : 'myPackage'
      packageSizeConfirmedLabel = t('inputs.packageSizeConfirmed.label', {
        myParcel: t(`inputs.packageSizeConfirmed.${myParcelKey}`),
        weight: formatNumber(weight),
        size,
      })
    }
  }

  return (
    <FormikForm noValidate>
      <Card>
        <CardHeader title={t('myDetails')} />
        <Divider />
        <CardContent>
          <Grid container direction="row" spacing={2} size={{ xs: 12 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <AddressDetails selectedCountryCode={countryCode.key} countryCodes={allowedCountries} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ContactDetails showDescriptionField={false} showReferenceField />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <TransportTypeInput
            returnPortal={returnPortal}
            countryCode={countryCode.key}
            showRequiredDataLabel={!isAddressAndContactValid}
          />
          <ReturnTypeInput
            returnPortal={returnPortal}
            countryCode={countryCode.key}
            transportType={transportType}
            showRequiredDataLabel={!isAddressAndContactValid || !transportType}
            isAddressAndContactValid={isAddressAndContactValid}
            showLabelTypeInput={showLabelTypeInput}
          />
          {showLabelTypeInput && (
            <LabelTypeInput
              returnPortal={returnPortal}
              countryCode={countryCode.key}
              showRequiredDataLabel={!isAddressAndContactValid}
            />
          )}
          {isAddressAndContactValid && !!transportType && (
            <>
              {returnType === 'consumer_pickup' && (
                <PaperSection title={t('choosePickup')}>
                  <PickupDateInput
                    sm
                    xs
                    label={t('pickupDateAndTime')}
                    dateFieldName="pickupDate"
                    timeWindowStartFieldName="pickupTimeWindowStart"
                    timeWindowEndFieldName="pickupTimeWindowEnd"
                    countryCode={countryCode.key}
                    postalCode={postalCode}
                    useEveningTimeWindows={eveningTimeWindows}
                    excludedWeekDays={[WeekDay.Sunday]}
                  />
                </PaperSection>
              )}
              {returnType === 'pickup' && (
                <PaperSection title={t('choosePickup')}>
                  <Grid container direction="row" spacing={2} size={{ xs: 12 }}>
                    <PickupDateInput
                      sm={false}
                      xs
                      label={t('pickupDate')}
                      dateFieldName="pickupDate"
                      countryCode={countryCode.key}
                      postalCode={postalCode}
                      useEveningTimeWindows={false}
                      excludedWeekDays={[WeekDay.Saturday, WeekDay.Sunday]}
                    />
                    {transportType === 'pallet' && (
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <NumberFieldInput name="pallets" label={t('inputs.pallets')} fullWidth required />
                      </Grid>
                    )}
                    <Grid size={{ xs: transportType === 'pallet' ? 6 : 12, sm: transportType === 'pallet' ? 3 : 6 }}>
                      <NumberFieldInput name="packages" label={t('inputs.packages')} fullWidth required />
                    </Grid>
                  </Grid>
                </PaperSection>
              )}
            </>
          )}
          <Grid
            container
            direction="column"
            spacing={2}
            sx={({ spacing }) => ({
              marginTop: spacing(3),
            })}
          >
            {weight && size && (
              <Grid>
                {getCheckBox(
                  'packageSizeConfirmed',
                  packageSizeConfirmedLabel,
                  !isCcc && returnType === 'service_point' ? (
                    <Tooltip title={t('inputs.packageSizeConfirmed.volume_info')}>
                      <span>
                        <InfoColored
                          circleFillColor={theme.palette.info.main}
                          letterFillColor={theme.palette.common.white}
                          sx={{ width: '20px', height: '20px', marginBottom: -0.5, marginLeft: 0.75 }}
                        />
                      </span>
                    </Tooltip>
                  ) : undefined
                )}
              </Grid>
            )}
            <Grid>
              {getCheckBox(
                'termsConfirmed',
                <Trans<string, DefaultNamespace, undefined, TranslationContext['context'], TranslationContext>
                  i18nKey="inputs.termsConfirmed.label"
                  components={tComponents}
                  tOptions={translationContext}
                />
              )}
            </Grid>
            <Grid>
              <CaptchaInput name="captcha" language={i18n.language} sitekey={config.captchaV2SiteKey} />
              {getErrorMessage('captcha')}
            </Grid>
            <Grid
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                data-test="submit"
                sx={{
                  marginTop: 1,
                  padding: 2,
                }}
              >
                {t('form.submit.requestServicePointReturnLabel')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </FormikForm>
  )
}
